import {Component, ElementRef, EventEmitter, HostBinding, Input, NgZone, OnChanges, Output, QueryList, SimpleChanges, TemplateRef, ViewChild, ViewChildren} from '@angular/core';
import {SubscribedComponent} from "../subscribed.component";
import {TranslateService} from "@ngx-translate/core";
import {ColDef, ColType} from "../interfaces/colDef";
import {TableOptions, TableOptionsComponent} from "../settings/table-options/table-options.component";
import {FilterDialogComponent} from "../dialogs/filter-dialog/filter-dialog.component";
import {PopupDialogComponent} from "../dialogs/popup-dialog/popup-dialog.component";
import {NbDialogService, NbThemeService, NbMediaBreakpoint, NbMenuItem, NbPopoverDirective} from "@nebular/theme";
import {TreeNode} from "../interfaces/treenode";
import {BehaviorSubject, Subject} from "rxjs";
import {Paged} from "../interfaces/paged";
import {CdkDragDrop} from "@angular/cdk/drag-drop";
import {SettingsService} from "../shared/settings.service";
import {environment} from "../../environments/environment";
import {APIService} from "../shared/api.service";
type LatLng = google.maps.LatLngLiteral;
import {DualAxisPagingMode, Globals} from "../shared/Globals";
import {ActivatedRoute, Router} from "@angular/router";

// Filter keys set by tabs / pickers / search — NOT user-added column filters.
// Excluded from the active-filter pills and the toolbar count badge.
export const SYNTHETIC_FILTER_KEYS = ['status', 'user_id', 'dates', 'stuff'];

/** Real (column) filter keys currently applied — excludes synthetic keys and
 * null/undefined values. */
export function activeFilterKeys(filter: any): string[] {
  if (!filter) { return []; }
  return Object.keys(filter).filter(
    k => !SYNTHETIC_FILTER_KEYS.includes(k) && filter[k] !== null && filter[k] !== undefined
  );
}

export interface DualAxisPageLoadedEvent {
  page: number;
  cause: 'navigation' | 'reset';
}

export enum DualAxisEvent{
  NOTHING,
  OPENOPTIONS,
  OPENFILTERS,
  OPENCOLUMNS,
  PAGE,
  RESET,
  /** Reload the page currently on screen after a row-level mutation. RESET is
   *  for a changed query and deliberately returns to the first page. */
  REFRESH,
  MAPCOLUMNS
}

export interface Editable<T>{
  edit?: (row: TreeNode<T>) => void,
  delete?: (row: TreeNode<T>) => void,
  archive?: (row: TreeNode<T>) => void,
  unarchive?: (row: TreeNode<T>) => void,
  sort?: (row: TreeNode<T>, from: number, to: number) => void,
  view?: (row: TreeNode<T>) => void,
  manageTeam?: (row: TreeNode<T>) => void,
  canEdit?: (row: TreeNode<T>) => boolean,
  canView?: (row: TreeNode<T>) => boolean,
  canDelete?: (row: TreeNode<T>) => boolean,
  canArchive?: (row: TreeNode<T>) => boolean,
  canUnarchive?: (row: TreeNode<T>) => boolean
}

export interface DualAxisDelegate<T>{
  header?: TreeNode<T|any>[];
  headerCellTemplateOutlet?: TemplateRef<any>;
  data: TreeNode<T>[],
  cellTemplateOutlet: TemplateRef<any>,
  cardTemplateOutlet?: TemplateRef<any>,   // mobile card-mode: opt-in stacked card per row
  forceCards?: boolean,                    // render cards at ALL widths (requires cardTemplateOutlet)
  openCellTemplateOutlet?: TemplateRef<any>,
  stickyColumnsLeft: ColDef[],
  standardColumns: ColDef[],
  stickyColumnsRight: ColDef[],
  paged: (page: number, pageSize: number) => Promise<Paged<T>>,
  exported?: (options: TableOptions, tag: string) => Promise<void>,
  options?: TableOptions,
  optionsChange?: (newvalue: any) => boolean,
  events?: Subject<DualAxisEvent>,
  sorter?: ((a: TreeNode<T>, b: TreeNode<T>) => number)|string,
  editable?: Editable<T>,
  selectRow?: ($event: MouseEvent, val: TreeNode<T>) => void
  rowClass?: (row: T) => string | null;
  /** Stable row identity for @for. Supply this when the delegate rebuilds its
   *  TreeNode wrappers on every fetch, so a refresh reuses the existing DOM
   *  (and the focus inside it) instead of recreating every row. */
  trackRow?: (node: TreeNode<T>) => any;
}

@Component({
    selector: 'bst-dual-axis',
    templateUrl: './dual-axis.component.html',
    styleUrls: ['./dual-axis.component.scss'],
    standalone: false
})
export class DualAxisComponent<T> extends SubscribedComponent implements OnChanges {

  @ViewChild('axis') readonly axis: ElementRef;

  @Input() readonly bootstrap: boolean = true;
  @Input() readonly delegate: DualAxisDelegate<T>;
  @Input() readonly height: string = null;
  @Input() readonly tag: string = null;
  @Input() readonly export: string = null;
  @Input() readonly pagingMode: DualAxisPagingMode = Globals.InfiniteMode;
  /** Staging spike (#1465 follow-up): renders the CSS Grid + subgrid layout instead
   *  of the <table>, replacing DualAxisDirective's translateX pin with two native
   *  sticky wrapper columns. No JS scroll handler drives the pin in this mode.
   *  Deliberately opt-in and NOT feature-complete: unlike the table branch, it does
   *  not support delegate.editable.sort (row drag), delegate.header (extra header
   *  rows) or delegate.openCellTemplateOutlet (expandable rows). Only flip this on
   *  a delegate that uses none of those -- Market Overview is the only one today. */
  @Input() readonly experimentalGrid: boolean = false;
  /** experimentalGrid only: the grid is exactly as wide as the scroller instead of
   *  max-content, so an fr track takes the free width and the rest fit; it still
   *  scrolls inside the scroller once the track minimums exceed that width. */
  @Input() @HostBinding('class.fit-width') fitWidth = false;
  @Output() pageLoaded = new EventEmitter<DualAxisPageLoadedEvent>();

  public readonly pageSizeOptions = [10, 25, 50, 100];
  public pageSize = this.settings.pageSize;
  public readonly appending: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public readonly page: BehaviorSubject<number> = new BehaviorSubject<number>(1);
  public pageResult: Paged<T> | null = null;
  /** Set when the newest load failed, so the empty state is not shown for a
   *  request that never returned any rows to begin with. */
  public readonly loadFailed: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  /** Identifies the newest in-flight paged() call. Only that call may write
   *  pageResult, page, loading, the failure state or the URL; an older response
   *  arriving afterwards is discarded. Note this does NOT cover the rows:
   *  delegates assign their own `data` inside paged(), before this check is
   *  reached, so a delegate that needs the same protection must guard it itself
   *  (see market-vacancies-list.component.ts's pagedGeneration). */
  private pageRequestToken = 0;
  /** Set by refresh() so the load it triggers keeps the reader's scroll offset.
   *  Every other load replaces the content wholesale and starts at the top --
   *  including a changed query that happens to stay on page 1, whose rows are
   *  entirely different. */
  private keepScrollForNextLoad = false;
  /** The page the newest request asked for, so retry() can resume after a failed
   *  page zeroed the infinite-scroll cursor to stop it re-firing. */
  private lastRequestedPage = 1;

  public readonly position = {
    scrollLeft: 0
  };

  public columns = [];
  public isMobile = false;
  private readonly mobileBreakpoints = ['xs', 'is', 'sm'];
  dragging: TreeNode<T>;
  private restoredOptions: TableOptions = null;

  private destroyed = false;

  constructor(private translate: TranslateService,
              private settings: SettingsService,
              private api: APIService,
              private dialogService: NbDialogService,
              private router: Router,
              private route: ActivatedRoute,
              private themeService: NbThemeService,
              private zone: NgZone) {
    super();
  }

  /** True when the delegate exposes any row action (drives the single kebab column). */
  get hasRowMenu(): boolean {
    const e = this.delegate?.editable;
    return !!(e && (e.view || e.edit || e.archive || e.unarchive || e.delete || e.manageTeam));
  }

  /** True when the panel is opened over a card-rendered view (mirrors the template predicate). */
  get isCardView(): boolean {
    return !!this.delegate?.cardTemplateOutlet && (this.isMobile || !!this.delegate?.forceCards);
  }

  /** Build the kebab items for one row, hiding actions the row can't currently do. */
  rowMenu(node: TreeNode<any>): NbMenuItem[] {
    const e = this.delegate?.editable;
    if (!e) { return []; }
    const items: NbMenuItem[] = [];
    const add = (fn: any, can: any, action: string, titleKey: string, icon: string) => {
      if (!fn) { return; }
      if (can && !can.call(this.delegate, node)) { return; }
      items.push({ title: this.translate.instant(titleKey), icon, data: { action, node } });
    };
    add(e.view, e.canView, 'view', 'view', 'external-link');
    add(e.edit, e.canEdit, 'edit', 'edit', 'edit-2-outline');
    add(e.manageTeam, undefined, 'manageTeam', 'manage_team', 'people-outline');
    add(e.archive, e.canArchive, 'archive', 'archive', 'archive-outline');
    add(e.unarchive, e.canUnarchive, 'unarchive', 'unarchive', 'undo-outline');
    add(e.delete, e.canDelete, 'delete', 'delete', 'trash-2-outline');
    return items;
  }

  // One NbPopover per row kebab; @ViewChildren collects them so rowMenuAction can
  // close the open one (template refs don't cross the <ng-template> boundary).
  // Scoped to the #rowKebabPop ref — NOT @ViewChildren(NbPopoverDirective) — so it
  // never captures the delegate's match-score popovers rendered via ngTemplateOutlet.
  @ViewChildren('rowKebabPop', { read: NbPopoverDirective }) private rowPopovers: QueryList<NbPopoverDirective>;

  /** Dispatch a row-kebab action to the matching delegate method (called from the
   * NbPopover template's direct (click) — no NbMenuService Subject, so no Chrome
   * overlay-teardown race, and the click is already inside Angular's zone). */
  rowMenuAction(action: string, node: TreeNode<any>): void {
    this.rowPopovers?.forEach(p => p.hide());
    const ed: any = this.delegate?.editable;
    if (!action || !node || !ed || typeof ed[action] !== 'function') { return; }
    ed[action].call(this.delegate, node);
  }

  /**
   * Card-row click handler. Delegates to the delegate's selectRow, but ignores clicks
   * that originate inside the row kebab, so opening the actions menu never ALSO fires
   * selectRow (which for some delegates — e.g. PendingApprovals — opens a detail dialog).
   * We deliberately do NOT stopPropagation on the kebab button: NbPopover's click-trigger
   * strategy listens at document level, so stopping the bubble would keep the popover from
   * ever opening. Guarding here decouples the kebab from selectRow for every delegate,
   * regardless of whether that delegate's selectRow is inert.
   */
  cardRowClick(event: MouseEvent, node: TreeNode<any>): void {
    if ((event.target as HTMLElement)?.closest?.('.card-kebab')) { return; }
    this.delegate?.selectRow?.(event, node);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.bootstrap?.currentValue && !changes.bootstrap?.previousValue) {
      this.bootstrapLoad();
    }
  }

  ngOnInit(): void {
    if(!this.delegate){
      throw new Error('Dual Axis needs a delegate!');
    }

    super.ngOnInit();
    this.mapColumns();

    if(this.delegate.events) {
      this.subs.push(this.delegate.events.subscribe(async (event) => {
        switch (event) {
          case DualAxisEvent.OPENOPTIONS:
            return this.openOptions();
          case DualAxisEvent.OPENFILTERS:
            return this.openOptions('filters');
          case DualAxisEvent.OPENCOLUMNS:
            return this.openOptions('columns');
          case DualAxisEvent.PAGE:
            await this.pageit();
            break;
          case DualAxisEvent.REFRESH:
            await this.refresh();
            break;
          case DualAxisEvent.MAPCOLUMNS:
            this.mapColumns();
          case DualAxisEvent.RESET:
            await this.reset();
            break;
        }
      }));
    }

    // React to browser back/forward changing ?page= on the same tab.
    this.subs.push(this.route.queryParamMap.subscribe(async qp => {
      const page = parseInt(qp.get('page') || '1', 10);
      const target = Number.isFinite(page) && page > 0 ? page : 1;
      if (this.pagingMode === Globals.PagesMode && this.pageResult && target !== this.pageResult.current_page) {
        await this.goToPage(target);
      }
    }));

    // Mobile flag drives card-mode (same pattern as app.component); onMediaQueryChange
    // only fires on a crossing, so seed from innerWidth.
    this.isMobile = window.innerWidth < 768;
    this.subs.push(this.themeService.onMediaQueryChange()
      .subscribe(([, current]: NbMediaBreakpoint[]) => {
        this.isMobile = this.mobileBreakpoints.includes(current.name);
      }));

    if(this.bootstrap){
      this.bootstrapLoad().then();
    }
  }

  /** Re-run the query from the first page. Search, filter, sort and scope
   *  changes all land here: they invalidate the current page, and keeping it
   *  would strand the viewer past the end of a narrower result set. The first
   *  load uses bootstrapLoad() instead, which honours a deep-linked ?page=. */
  async reset(){
    this.restoreSavedOptions();
    await this.goToPage(1);
  }

  /** First load for this delegate: honour ?page= so a bookmarked or shared link
   *  opens on the page it names. Only PagesMode ever writes that parameter. */
  private async bootstrapLoad(){
    this.restoreSavedOptions();
    const page = parseInt(this.route.snapshot.queryParamMap.get('page') || '1', 10);
    await this.goToPage(Number.isFinite(page) && page > 0 ? page : 1);
  }

  /** Restore saved sorts and filters when options object changes (new or re-created) */
  private restoreSavedOptions(){
    if(this.tag && this.delegate.options && this.restoredOptions !== this.delegate.options){
      this.restoredOptions = this.delegate.options;
      const savedSorts = this.settings.userSetting('table_' + this.tag + '_sorts');
      if(savedSorts){
        this.delegate.options.sorts = savedSorts;
      }
      const savedFilters = this.settings.userSetting('table_' + this.tag + '_filters');
      if(savedFilters && Object.keys(savedFilters).length){
        this.delegate.options.filter = {...(this.delegate.options.filter || {}), ...savedFilters};
      }
    }
  }

  /** Load the current page. Returns true when this call was still the newest
   *  one when its response arrived, i.e. when it owns the resulting state. */
  private async pageit(): Promise<boolean> {
    // Consume the caller's keep-scroll intent up front. It has to be cleared
    // before every return below, not just the one that uses it, or a refresh
    // whose request is superseded (or that returns early) leaves the flag set
    // and an unrelated later load silently inherits a stale scroll offset.
    const keepScroll = this.keepScrollForNextLoad;
    this.keepScrollForNextLoad = false;

    if(!this.delegate || !this.page.getValue()) { return false; }

    const token = ++this.pageRequestToken;
    const requestedPage = this.page.getValue();
    this.lastRequestedPage = requestedPage;
    this.loading.next(true);
    this.loadFailed.next(false);
    try {
      const result = await this.delegate.paged(requestedPage, this.pageSize);
      // A newer request started while this one was in flight; it owns the state
      // now, so drop this response rather than overwriting the newer one.
      if (token !== this.pageRequestToken) { return false; }

      // A non-null result without a rows array is a delegate handing back
      // something that is not a page — an outright failure wearing a
      // success's clothes.
      //
      // `null`/`undefined` is deliberately NOT treated as a failure: the sentinel
      // is ambiguous in this codebase. table-object and cv-list return null when
      // they decline to fetch at all (not yet configured), while cv-list's own
      // catch returns it after a fetch failed. Since paged() cannot distinguish
      // those, flagging a failure here would put "couldn't load this page" on
      // screens that never made a request. Delegates that swallow their errors
      // need to reject instead; tracked as a follow-up.
      if (result !== null && result !== undefined && !Array.isArray(result.data)) {
        this.loadFailed.next(true);
        this.haltInfinitePaging();
        return false;
      }

      this.pageResult = result;

      if(this.pagingMode !== Globals.PagesMode){
        if (result?.data?.length) {
          this.page.next(this.page.getValue() + 1);
        } else {
          this.page.next(0);
        }
      }
      else{
        // Removing rows can shrink the result set out from under the current
        // page (hiding the last row on the last page). Fall back to the last
        // page that still exists instead of rendering an empty one.
        const clamped = this.clampTarget(result);
        if (clamped !== null && clamped !== requestedPage) {
          // Replace rather than push: the out-of-range page is still the current
          // history entry, so pushing would let Back return to it, land out of
          // range again, and clamp again -- trapping the reader in a loop.
          await this.goToPage(clamped, true);
          return false;
        }
        // An in-place refresh (after hiding a row, say) keeps the reader where
        // they were; anything else is new content and starts at the top.
        if (this.axis?.nativeElement && !keepScroll) {
          this.axis.nativeElement.scrollTop = 0;
        }
      }
      return true;
    } catch (error) {
      if (token !== this.pageRequestToken) { return false; }
      // Render a failure instead of letting the rejection escape unhandled and
      // leaving the empty state to imply the query simply matched nothing.
      console.error(error);
      this.loadFailed.next(true);
      this.haltInfinitePaging();
      // Report the page as unowned so goToPage() does not write the failed page
      // into the URL. pageResult still holds the last page that actually loaded,
      // so the ?page= listener below would compare the new URL against that stale
      // page and re-issue the request that just failed.
      return false;
    } finally {
      // Always clear loading/appending for the newest request, even if
      // delegate.paged() rejects, so a failed request can't strand a permanent
      // loading state. With the list now cleared before the await (#557), a
      // stuck loading flag would otherwise leave the new loading spinner
      // spinning forever on an error. An older request clears nothing: the
      // newer one it was superseded by is still running.
      if (token === this.pageRequestToken) {
        this.loading.next(false);
        this.appending.next(false);
      }
    }
  }

  /** Stop infinite scroll advancing after a failed page. scroll() re-fires from
   *  the cursor every time the reader nears the bottom, so leaving it in place
   *  repeats the failing request indefinitely. retry() restores it. */
  private haltInfinitePaging(): void {
    if (this.pagingMode !== Globals.PagesMode) {
      this.page.next(0);
    }
  }

  /** Page to fall back to when the loaded page sits past the end of the result
   *  set, or null when the page is valid. */
  private clampTarget(result: Paged<T> | null): number | null {
    if (!result || !result.last_page || !result.current_page) { return null; }
    return result.current_page > result.last_page ? result.last_page : null;
  }

  /** Reload the page currently on screen, keeping the reader's place. Use this
   *  after a row-level mutation; reset() is for a changed query and starts over
   *  at the first page. Out-of-range recovery applies, so deleting the last row
   *  on the last page lands on the last page that still exists. */
  public async refresh(): Promise<void> {
    this.keepScrollForNextLoad = true;
    // `page` holds the requested page and, unlike pageResult, is not left behind
    // by a failed load. In infinite mode there is no page to hold on to -- `page`
    // is a cursor -- so start over, which delegates handle by clearing their rows
    // at page <= 1.
    await this.goToPage(this.pagingMode === Globals.PagesMode ? (this.page.getValue() || 1) : 1);
  }

  /** Re-run the current page after a failed load. */
  public async retry(): Promise<void> {
    if (this.pagingMode === Globals.PagesMode) {
      // The page the reader asked for, not the last one that loaded: on a failed
      // load pageResult still holds the previous page, so retrying from it would
      // quietly fetch something other than what failed.
      await this.goToPage(this.page.getValue() || 1);
    } else {
      // A failed page zeroes the cursor to stop scroll re-firing it, so put the
      // failed page back before re-running it.
      this.page.next(this.lastRequestedPage || 1);
      await this.pageit();
    }
  }

  /** Row identity for @for. Defaults to the node itself, which is what this
   *  component tracked before delegates could opt in. */
  public rowTrack(node: TreeNode<T>): any {
    return this.delegate?.trackRow ? this.delegate.trackRow.call(this.delegate, node) : node;
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    super.ngOnDestroy();
  }

  public async goToPage(page: number, replaceUrl = false): Promise<void> {
    this.page.next(page);
    const owned = await this.pageit();

    // A superseded or failed request must not rewrite the URL either: the newer
    // one already put its own page there. Nor may a request that outlived the
    // component: the host navigated away mid-fetch, and a navigate relative to
    // the dead route would yank the user back to this list.
    if(owned && !this.destroyed && this.pagingMode === Globals.PagesMode) {
      await this.router.navigate([], {
        relativeTo: this.route,
        queryParams: {page: this.page.getValue() > 1 ? this.page.getValue() : null},
        queryParamsHandling: 'merge',
        replaceUrl,
      });
    }
  }

  public async scrolled(){
    if(!this.appending.getValue() && this.page.getValue() > 0){
      this.appending.next(true);
      try{
        await this.pageit();
      } catch (error){
        console.error(error);
      }

      this.appending.next(false);
    }
  }

  public scroll($event: Event) {
    // experimentalGrid + PagesMode (Market Overview, the only consumer today) needs
    // neither branch below: table-object's expanded-row scrollLeft binding is a
    // production-table-only consumer this branch never renders, and PagesMode pages
    // via the pager, not scroll distance-from-bottom. Skip the zone re-entry entirely
    // instead of paying for a no-op update on every scroll tick (#1465 follow-up,
    // reported as sluggish horizontal scrolling in Safari).
    if (this.experimentalGrid && this.pagingMode === Globals.PagesMode) { return; }
    if($event?.target){
      const target = (<HTMLDivElement>($event.target));
      // axisScroll fires outside the Angular zone, so nothing here ticks change
      // detection by itself. A horizontal scroll re-enters it: table-object binds
      // position.scrollLeft to keep its expanded Users row in view. A vertical
      // scroll has no template consumer and stays out, which is the point.
      if (this.position.scrollLeft !== target.scrollLeft) {
        this.zone.run(() => this.position.scrollLeft = target.scrollLeft);
      }

      const distanceFromBottom = target.scrollHeight - target.scrollTop - target.clientHeight;
      const threshold = Math.max(target.clientHeight * 0.3, 50);
      if (this.pagingMode === Globals.InfiniteMode && distanceFromBottom <= threshold
        && !this.appending.getValue() && this.page.getValue() > 0) {
        this.zone.run(() => this.scrolled());
      }
    }
  }


  filter(col: ColDef, $event: MouseEvent){
    $event.stopPropagation();
    $event.preventDefault();

    const data = {};
    data[col.name] = this.delegate.options && this.delegate.options.filter && typeof this.delegate.options.filter[col.name] !== 'undefined' ? this.delegate.options.filter[col.name] : null;

    this.subs.push(this.dialogService.open<FilterDialogComponent>(FilterDialogComponent, {
      context: {
        title: col.title,
        description: [col],
        data: data
      }
    }).onClose.subscribe(async o => {
      if(o !== null) {
        if(!this.delegate.options.filter){
          this.delegate.options.filter = {};
        }

        for(let k in o){
          if(o.hasOwnProperty(k)) {
            if (o[k] === null) {
              delete this.delegate.options.filter[k];
            } else {
              this.delegate.options.filter[k] = o[k];
            }
          }
        }

        if(this.tag){
          const filtersToSave = {...this.delegate.options.filter};
          delete filtersToSave['stuff'];
          delete filtersToSave['status'];
          this.settings.putUserSetting('table_' + this.tag + '_filters', filtersToSave);
        }

        await this.reset();
      }
    }));
  }

  public changePageSize(size: number): void {
    if (size === this.pageSize) return;
    this.pageSize = size;
    this.settings.savePageSize(size);
    this.settings.saveUserSettings(true)
      .then(() => this.goToPage(1));
  }

  /** Remove a single active filter (pill ×). Mirrors the delete path in filter(). */
  async clearFilter(colName: string) {
    if (this.delegate.options?.filter && colName in this.delegate.options.filter) {
      delete this.delegate.options.filter[colName];
      if (this.tag) {
        const filtersToSave = { ...this.delegate.options.filter };
        delete filtersToSave['stuff'];
        delete filtersToSave['status'];
        this.settings.putUserSetting('table_' + this.tag + '_filters', filtersToSave);
      }
      await this.reset();
    }
  }

  async sort(col: ColDef, $event: MouseEvent) {
    if(!this.loading.getValue() && this.sortable(col)) {
      let index = -1;
      let s = this.delegate.options.sorts.find((s, i) => {
        index = i;
        return s.col === col.sort;
      });
      if(!s){
        s = this.delegate.options.sorts.find((s, i) => {
          index = i;
          return s.col === col.name;
        });
      }

      if (!s) {
        if (!$event.shiftKey) {
          this.delegate.options.sorts = [];
        }
        this.delegate.options.sorts.push({col: this.sortKey(col), dir: 1});
      } else if (s.dir === 1) {
        s.dir = 2;
      } else if (index >= 0) {
        this.delegate.options.sorts.splice(index, 1);
      }

      await this.persistSortsAndReload();
    }
  }

  /** The key stored in options.sorts[].col: a string `sort` alias when set, else the column name. */
  private sortKey(col: ColDef): string {
    return typeof col.sort === 'string' ? col.sort : col.name;
  }

  /** 2 = desc, 1 = asc. Numbers/currency/dates/bools default to highest/newest/true first;
   *  text A→Z. Prevents the "worst match first" surprise when selecting match_score
   *  (ColType.INT), and makes a bool column (e.g. New) sort true-first on the first click. */
  private defaultSortDir(col: ColDef): number {
    return [ColType.INT, ColType.CURRENCY, ColType.DATE, ColType.BOOL].includes(col.type) ? 2 : 1;
  }

  /** Persist current sorts to the per-tag user setting and re-fetch. Shared by header sort() and panel setSort(). */
  private async persistSortsAndReload(): Promise<void> {
    if (this.tag) {
      this.settings.putUserSetting('table_' + this.tag + '_sorts', this.delegate.options.sorts);
    }
    try {
      await this.reset();
    } catch (e) {
      console.error(e);
    }
  }

  /** Panel "Sort by": set this column as the sole primary sort with a type-aware default
   *  direction, or flip direction if it is already the active sort. Server re-fetch via reset(). */
  async setSort(col: ColDef): Promise<void> {
    if (this.loading.getValue() || !this.delegate.options?.sorts || col.sort === false) { return; }
    if (this.delegate.editable && this.delegate.editable.sort) { return; } // drag-reorder host: leave sort to headers
    const cur = this.delegate.options.sorts[0];
    const active = cur && (cur.col === col.sort || cur.col === col.name);
    const dir = active ? (cur.dir === 1 ? 2 : 1) : this.defaultSortDir(col);
    this.delegate.options.sorts = [{ col: this.sortKey(col), dir }];
    await this.persistSortsAndReload();
  }

  /** This column's entry in options.sorts at any rank (shift-click stacks keys), or null. */
  private sortEntry(col: ColDef): { dir: number, index: number } | null {
    const sorts = this.delegate.options?.sorts ?? [];
    const index = sorts.findIndex(s => s.col === col.sort || s.col === col.name);
    return index < 0 ? null : { dir: sorts[index].dir, index };
  }

  /** Sort direction at any rank, or false. Drives the single directional arrow. */
  sorted(col: ColDef){
    return this.sortEntry(col)?.dir ?? false;
  }

  /** Only the primary (first) key gets the accent and aria-sort. A stacked
   *  secondary key keeps its arrow in the idle color, so it stays visible
   *  without looking like, or announcing as, the primary sort. */
  sortPrimary(col: ColDef): boolean {
    return this.sortEntry(col)?.index === 0;
  }

  /** Mirrors the sort() guard so the header only advertises sorting where it works. */
  sortable(col: ColDef): boolean {
    return !!this.delegate.options?.sorts
      && col.sort !== false
      && !(this.delegate.editable && this.delegate.editable.sort);
  }

  sortAria(col: ColDef): 'ascending' | 'descending' | null {
    if (!this.sortable(col) || !this.sortPrimary(col)) { return null; }
    const direction = this.sorted(col);
    return direction === 1 ? 'ascending' : direction === 2 ? 'descending' : null;
  }

  filtervalid(d: ColDef): boolean{
    return this.delegate.options && this.delegate.options.filter && typeof this.delegate.options.filter[d.name] !== 'undefined';
  }

  mapColumns(){
    if(this.delegate) {
      if (this.delegate.options) {
        let fast = {};
        for (let i in this.delegate.options.display) {
          const data = this.delegate.options.display[i];
          if (data.shown) {
            fast[data.col] = i;
          }
        }

        this.columns = this.delegate.standardColumns.filter(o => {
          return fast[o.name];
        }).sort((a: ColDef, b: ColDef) => {
          const ai = fast[a.name];
          const bi = fast[b.name];
          return ai - bi;
        });
      } else {
        this.columns = [].concat(this.delegate.standardColumns);
      }
    }
  }

  openOptions(initialSection: 'filters' | 'columns' = 'filters') {
    this.dialogService.open<PopupDialogComponent<TableOptionsComponent>>(PopupDialogComponent, {
      dialogClass: 'options-panel',
      context: {
        comp: TableOptionsComponent,
        title: 'Table options',
        componentContext: {
          delegate: this,
          colDefs: this.delegate.standardColumns,
          // otherDefs: others,
          hideColumns: !!this.delegate.forceCards, // card mode: column show/hide is meaningless
          hideSort: !this.isCardView,              // sort only shown in card view
          // Sort must offer sticky columns too (e.g. the Projects/Clients name lives in
          // stickyColumnsLeft), unlike the Columns show/hide section which uses standardColumns only.
          sortColDefs: [
            ...(this.delegate.stickyColumnsLeft || []),
            ...(this.delegate.standardColumns || []),
            ...(this.delegate.stickyColumnsRight || []),
          ],
          // The FILTER section likewise includes sticky columns so a filter on a sticky column
          // (e.g. the market list's New / Score) is visible + removable here. The component's
          // filterableColumns getter drops any col.filter === false, so non-filterable sticky
          // columns are excluded automatically.
          filterColDefs: [
            ...(this.delegate.standardColumns || []),
            ...(this.delegate.stickyColumnsLeft || []),
            ...(this.delegate.stickyColumnsRight || []),
          ],
          options: this.delegate.options,
          initialSection,
          onChange: async value => {
            const needsrefresh = this.delegate.optionsChange ? this.delegate.optionsChange(value) : false;
            this.delegate.options = value;
            this.mapColumns();

            // Save display to user settings
            if(this.tag){
              this.settings.putUserSetting('table_' + this.tag, this.delegate.options.display);
              this.settings.saveUserSettings(true).then();
            }

            if(needsrefresh){
              await this.reset();
            }
          },
          onExport: (!!this.delegate.exported || this.export) ? async value => {
            value.sorts = await this.sorts();
            if(typeof this.delegate.exported === 'function') {
              await this.delegate.exported(value, this.export);
            }
            else if(this.export){
              const allcols = (this.delegate.stickyColumnsLeft || []).concat(this.columns).concat(this.delegate.stickyColumnsRight || []);
              const mycols = [];
              for (let i=0; i<allcols.length; i++) {
                if(!allcols[i].noexport) {
                  const title = await this.translate.get(allcols[i].title || allcols[i].name).toPromise();
                  mycols.push({
                    name: allcols[i].name,
                    title,
                    type: allcols[i].type
                  });
                }
              }
              this.api.postForm(environment.api + '/export/' + this.export, {...value, ...{columns: mycols}});
            }
          } : null
        }
      }
    });
  }

  dropped($event: CdkDragDrop<T, any>) {
    if(this.delegate.editable && this.delegate.editable.sort){
      this.delegate.editable.sort.call(this.delegate, this.dragging, $event.previousIndex+1, $event.currentIndex+1);
      this.dragging = null;
    }
  }

  public async sorts(){
    const _sorts:{col: string, dir: number, meta?: any}[] = [];
    if(this.delegate.options?.sorts) {
      for (let s of this.delegate.options?.sorts) {
        const col = this.delegate.standardColumns.concat(this.delegate.stickyColumnsLeft || [], this.delegate.stickyColumnsRight || []).find(si => si.name === s.col);
        if (col) {

          let location: LatLng = null;
          if (col.type === ColType.LOCATION) {
            this.loading.next(true);
            location = await Globals.getLocation();
            this.loading.next(false);
          }

          if (col.sort instanceof Array) {
            for (let so of col.sort) {
              _sorts.push({col: so, dir: s.dir, meta: location});
            }
          } else if (col.name) {
            _sorts.push({
              col: col.name,
              dir: s.dir,
              meta: location
            });
          }
        }
      }
    }
    return _sorts;
  }

  get collength(): number{
    return this.columns.length
      + (this.delegate?.stickyColumnsLeft?.length || 0)
      + (this.delegate?.stickyColumnsRight?.length || 0)
      + this.extralength;
  }

  get extralength(): number{
    // One kebab column for all row actions (view/edit/archive/unarchive/delete)
    // plus the optional drag-sort handle column.
    return (this.hasRowMenu ? 1 : 0)
      + (this.delegate?.editable?.sort ? 1 : 0);
  }

  // --- experimentalGrid sizing (see the @Input above) ------------------------
  // Grid column tracks in document order: sticky-left, then the visible middle
  // columns, then sticky-right. Middle/left columns get the table branch's own
  // 125px floor and grow with content (max-content mirrors table auto-layout
  // closely enough for this spike); sticky-right keeps its exact stickyWidth so
  // headers like "Match score" still fit.
  get gridTemplateColumns(): string {
    const left = (this.delegate?.stickyColumnsLeft || []).map(() => 'minmax(125px, max-content)');
    const mid = this.columns.map(c => c.gridTrack || 'minmax(125px, max-content)');
    const right = (this.delegate?.stickyColumnsRight || []).map(s => `${s.stickyWidth || 130}px`);
    return [...left, ...mid, ...right].join(' ');
  }

  get gridLeftPinColumns(): string {
    return `repeat(${(this.delegate?.stickyColumnsLeft || []).length || 1}, minmax(125px, max-content))`;
  }

  get gridRightPinColumns(): string {
    const right = (this.delegate?.stickyColumnsRight || []).map(s => `${s.stickyWidth || 130}px`);
    return right.length ? right.join(' ') : '130px';
  }

  // Full-height span for both pin wrappers: header row + one row per data row.
  get gridPinRowSpan(): string {
    return `1 / ${(this.delegate?.data?.length || 0) + 2}`;
  }

  // Skeleton placeholders shown on the initial load (before any rows exist); the
  // template @for-s over these with plain index tracking (values are cosmetic).
  // skeletonRows is a fixed count -- keep it in sync with the hardcoded shimmer
  // cascade in dual-axis.component.scss (.sk-row:nth-child up to N+1).
  readonly skeletonRows: ReadonlyArray<number> = [0, 1, 2, 3, 4];

  // One skeleton cell per real column so bars align under the header; tracks the
  // live column count. Only read while loading (inside @if), so the per-call
  // allocation is negligible.
  get skeletonCols(): ReadonlyArray<number> {
    return Array.from({ length: Math.max(this.collength, 1) }, (_, i) => i);
  }
}
